import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';

// Default content (fallback)
//
// FIGURE-FREE ON PURPOSE. This literal is what a tenant sees before they have
// written any page content of their own, so it is published on behalf of a
// business whose terms are not known here. It previously carried one business's
// offer ("$99 Down Payment", "1-5 years") and every tenant without their own
// content row published it as theirs.
//
// These strings cannot be derived, because page content is free text an admin
// edits and stores verbatim -- there is no interpolation step between the
// site_settings row and the rendered card. Neutral is therefore the only honest
// default: it describes the shape of the offer without stating a figure, and the
// admin replaces it with their real numbers in Settings -> Page Content.
//
// Kept byte-identical to DEFAULT_HOME_CONTENT in server/server.js and in
// client/src/pages/PageContentSettings.js. All three are the same fallback seen
// from three places (public render, API, admin editor); if they drift, an admin
// edits copy that differs from what visitors are being shown.
const DEFAULT_HOME_CONTENT = {
  whyChooseUs: [
    { id: 1, icon: '💰', title: 'Low Down Payment', description: 'Flexible down payment options make land ownership accessible. Choose the plan that fits your budget.' },
    { id: 2, icon: '📋', title: 'Flexible Terms', description: 'Choose the payment term that works for you. No credit checks, no bank hassles.' },
    { id: 3, icon: '🎯', title: 'Simple Process', description: "Browse properties, choose your terms, and start making payments online. It's that easy." },
    { id: 4, icon: '🏆', title: 'No Hidden Fees', description: 'Transparent pricing with no prepayment penalties. Pay off early and save on interest.' }
  ],
  howItWorks: [
    { id: 1, step: 1, title: 'Browse Properties', description: 'Explore our available land parcels and find the perfect property for your needs.' },
    { id: 2, step: 2, title: 'Choose Your Terms', description: 'Use our calculator to select your down payment amount and loan term.' },
    { id: 3, step: 3, title: 'Complete Purchase', description: 'Sign up, make your down payment securely online, and receive your loan details.' },
    { id: 4, step: 4, title: 'Own Your Land', description: 'Make monthly payments through your dashboard and watch your equity grow.' }
  ]
};

/**
 * The configured term years, phrased for a marketing bullet.
 *
 * This line used to read a hardcoded "1-5 year terms" on every tenant's site,
 * which contradicted the tenant's own termOptions the moment they configured
 * anything else -- Sunny Acres offers 2, 3, 4, 5 and 7, Green Acres offers 1
 * through 10, and both were told 1-5.
 *
 * A contiguous run collapses to a range ("1-10 year terms"); a list with a gap
 * is spelled out, because "2-7" would advertise a 6-year term nobody can pick.
 */
const formatTermOptions = (years) => {
  const sorted = [...new Set((years || []).map(Number))]
    .filter(Number.isFinite)
    .sort((a, b) => a - b);

  if (sorted.length === 0) return null;
  if (sorted.length === 1) return `${sorted[0]} year terms`;

  const isContiguous = sorted.every((year, i) => i === 0 || year === sorted[i - 1] + 1);
  if (isContiguous) return `${sorted[0]}-${sorted[sorted.length - 1]} year terms`;

  return `${sorted.slice(0, -1).join(', ')} or ${sorted[sorted.length - 1]} year terms`;
};

function Home() {
  const { getCompanyName, getCompanyLegalName, getFinancingTiers, getTermOptions, getDisplayBranding } = useConfig();
  const financingTiers = getFinancingTiers();
  const termLabel = formatTermOptions(getTermOptions());
  const branding = getDisplayBranding();

  const [homeContent, setHomeContent] = useState(DEFAULT_HOME_CONTENT);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await api.get('/config/page-content');
        if (response.data.homeContent) {
          setHomeContent(response.data.homeContent);
        }
      } catch (err) {
        console.error('Failed to fetch page content:', err);
      }
    };
    fetchContent();
  }, []);

  return (
    <div className="home">
      {/* Hero Section with Logo or Text */}
      <section className="hero">
        <div className="hero-content">
          {branding.type === 'text' ? (
            <h1 className="hero-brand-text">{branding.text}</h1>
          ) : (
            <img
              src={branding.lightSrc || branding.src}
              alt={branding.type === 'deedstack' ? 'DeedStack' : getCompanyLegalName()}
              className="hero-logo"
              onError={(e) => {
                console.error('[Home] Hero logo failed to load:', e.target.src);
                e.target.style.display = 'none';
              }}
            />
          )}
          <h1 className="hero-title">Make Land Ownership Simple</h1>
          {/* NEUTRAL, not derived. The entry price is not reliably derivable
              here: a percent-only tenant has no dollar figure to quote at all
              (Dev Testing offers 25% down and nothing fixed), three of the six
              live tenants have no tiers configured, and this line renders above
              the fold BEFORE the config fetch resolves -- defaultConfig ships
              financingTiers: [], so a derived figure would either be missing on
              first paint or appear and then change under the reader. A hero
              claim has to be true in every one of those states. It previously
              read "starting at just $99 down" on every tenant's site. */}
          <p className="hero-subtitle">
            Own your piece of land with financing terms that work for you.
          </p>
          <div className="hero-buttons">
            <Link to="/properties" className="btn btn-primary">Browse Properties</Link>
            <Link to="/register" className="btn btn-secondary">Get Started</Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Why Choose {getCompanyName()}?</h2>

          <div className="features-grid">
            {homeContent.whyChooseUs.map((feature) => (
              <div key={feature.id} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">How It Works</h2>

          <div className="steps">
            {homeContent.howItWorks.map((step) => (
              <div key={step.id} className="step">
                <div className="step-number">{step.step}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Financing Options
          Hidden entirely when the business has not configured any tiers. The
          grid used to be the only thing driven by the data, so an unconfigured
          tenant published a heading promising "Multiple Payment Plans to Fit
          Your Budget" above an empty row. No offer is better than an empty
          promise. */}
      {financingTiers.length > 0 && (
      <section className="financing">
        <div className="container">
          <h2 className="section-title">Financing Options</h2>
          <p className="section-subtitle">Multiple Payment Plans to Fit Your Budget</p>

          <div className="financing-grid">
            {financingTiers.map((tier, index) => (
              <div key={index} className={`financing-card ${tier.isPopular ? 'featured' : ''}`}>
                {tier.isPopular && <div className="financing-badge">Most Popular</div>}
                <h3>{tier.name}</h3>
                <div className="financing-rate">{tier.apr}% APR</div>
                <ul className="financing-features">
                  {tier.downPayment ? (
                    <>
                      <li>Available on all properties</li>
                      <li>Get started today</li>
                      {termLabel && <li>{termLabel}</li>}
                      {/* This used to read "Payments as low as $X/month" from
                          minimumMonthlyPayment. That setting is a FLOOR, not an
                          offer: calculateFinancing() applies it as
                          Math.max(amortizedPayment, minimum), so it is only ever
                          the price of a loan small enough for the clamp to bite.
                          No listing on Sunny Acres can reach their $75 -- the
                          cheapest payment buyable on this very tier is about
                          $241, and about $150 across all their tiers. The claim
                          was written when the platform served one business whose
                          catalogue happens to make it true, and it silently
                          became false for everyone else. There is no derived
                          replacement, because the true answer depends on a
                          property and this card has none. */}
                      <li>No prepayment penalty</li>
                    </>
                  ) : (
                    <>
                      <li>{tier.apr <= 10 ? 'Best rate available' : 'Lower interest rate'}</li>
                      <li>Flexible terms</li>
                      <li>Build equity faster</li>
                      <li>No penalties</li>
                    </>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-content">
          <h2>Ready to Own Your Land?</h2>
          <p>Start browsing properties and find your perfect piece of land today.</p>
          <Link to="/properties" className="btn btn-large">View Available Properties</Link>
        </div>
      </section>
    </div>
  );
}

export default Home;